
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAuthState = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const checkUser = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      setError(error.message);
      return;
    }
    
    // Only redirect to home if user is logged in AND not in password reset flow
    if (session && !isResettingPassword) {
      setUserId(session.user.id);
      // Fetch user role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();
      
      if (roleError) {
        console.error('Error fetching role:', roleError);
      } else {
        setUserRole(roleData?.role || 'No role assigned');
      }
      
      // Only navigate to home if not in password reset flow
      navigate("/");
    }
  };

  useEffect(() => {
    // Skip authentication check when user is resetting password
    if (!isResettingPassword) {
      checkUser();
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event);
        
        // Only handle sign in if not in password reset flow
        if (event === "SIGNED_IN" && !isResettingPassword) {
          if (session) {
            setUserId(session.user.id);
            // Fetch user role
            const { data: roleData, error: roleError } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', session.user.id)
              .single();
            
            if (roleError) {
              console.error('Error fetching role:', roleError);
            } else {
              setUserRole(roleData?.role || 'No role assigned');
            }
            navigate("/");
          }
        }
        // Clear error when user signs out
        if (event === "SIGNED_OUT") {
          setError(null);
          setUserId(null);
          setUserRole(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, isResettingPassword]);

  return {
    error,
    setError,
    userId,
    setUserId,
    userRole,
    setUserRole,
    isResettingPassword,
    setIsResettingPassword
  };
};
